import { useEffect, useRef, useState, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';

export const useWavesurfer = (options = {}) => {
  const containerRef = useRef(null);
  const wavesurferRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isReady, setIsReady] = useState(false);

  const {
    height = 40,
    waveColor = '#d1d5db',
    progressColor = '#8b5cf6',
    cursorColor = '#8b5cf6',
    url,
    ...otherOptions
  } = options;

  useEffect(() => {
    if (!containerRef.current || !url) return;

    // Destrói instância anterior
    if (wavesurferRef.current) {
      wavesurferRef.current.destroy();
      wavesurferRef.current = null;
    }

    const ws = WaveSurfer.create({
      container: containerRef.current,
      height,
      waveColor,
      progressColor,
      cursorColor,
      barWidth: 2,
      barGap: 1,
      responsive: true,
      normalize: true,
      interact: true,
      backend: 'MediaElement', // 👈 usa MediaElementAudio para suporte nativo a preservesPitch
      mediaControls: false,
      ...otherOptions,
    });

    // Carrega o áudio
    ws.load(url);

    // Quando o áudio estiver pronto
    ws.on('ready', () => {
      setDuration(ws.getDuration());
      setIsReady(true);

      try {
        // Recupera o elemento de mídia usado internamente
        const media = ws.getMediaElement?.() || ws.media;

        if (media) {
          // ✅ Mantém a entonação original mesmo com playbackRate alterado
          media.preservesPitch = true;
          media.mozPreservesPitch = true;
          media.webkitPreservesPitch = true;

        }
      } catch (error) {
        console.warn('Não foi possível aplicar preservesPitch:', error);
      }
    });

    ws.on('audioprocess', () => setCurrentTime(ws.getCurrentTime()));
    ws.on('play', () => setIsPlaying(true));
    ws.on('pause', () => setIsPlaying(false));
    ws.on('finish', () => {
      setIsPlaying(false);
      ws.seekTo(0);
    });
    ws.on('error', (error) => {
      console.error('WaveSurfer error:', error);
      setIsReady(false);
    });

    wavesurferRef.current = ws;

    return () => ws.destroy();
  }, [url, height, waveColor, progressColor, cursorColor]);

  // Play / pause
  const playPause = useCallback(() => {
    if (wavesurferRef.current && isReady) {
      wavesurferRef.current.playPause();
    }
  }, [isReady]);

  // Controle de velocidade (mantém pitch)
  const setPlaybackRate = useCallback((rate) => {
    if (wavesurferRef.current && isReady) {
      try {
        const media = wavesurferRef.current.getMediaElement?.() || wavesurferRef.current.media;
        if (media) {
          media.playbackRate = Math.max(0.5, Math.min(2.5, rate)); // limita entre 0.5x e 2.5x
          // garante pitch constante
          media.preservesPitch = true;
          media.mozPreservesPitch = true;
          media.webkitPreservesPitch = true;
        } else {
          // fallback do próprio wavesurfer
          wavesurferRef.current.setPlaybackRate(rate);
        }
      } catch (error) {
        console.warn('Erro ao ajustar playbackRate:', error);
        wavesurferRef.current.setPlaybackRate(rate);
      }
    }
  }, [isReady]);

  return {
    containerRef,
    wavesurfer: wavesurferRef.current,
    isPlaying,
    currentTime,
    duration,
    isReady,
    playPause,
    setPlaybackRate,
  };
};
